import { createPublicOfferLinkRequest } from "../../../services/publicOfferLinksApi";
import { getUserInfo } from "../../../services/localStorage";
import { resolveAgentCompanyLogoUrl } from "../../../utils/resolveAgentCompanyLogoUrl";
import { mapLdsDealToCardVm } from "../adapters/mapLdsDealToCardVm";
import { buildOrganizedTourSmartOfferPayload } from "./buildOrganizedTourSmartOfferPayload";
import { computeOrganizedTourAgentQuoteCommission } from "./computeOrganizedTourAgentQuoteCommission";
import {
  resolveOrganizedTourProfileDisplayIls,
  resolveOrganizedTourSearchCardPackageIls,
  sanitizeOrganizedTourPackageIls,
} from "./resolveOrganizedTourSearchCardPackageIls";

export const ORGANIZED_TOUR_QUOTE_TERMS_FALLBACK_HE = `
המחיר אינו מהווה התחייבות עד להשלמת ההזמנה ואימות זמינות מול החברה.
תנאי ביטול, שינוי ותשלום לפי תנאי מארגן הטיול.
`.trim();

function buildAgentBrandingFromUser(user) {
  if (!user || typeof user !== "object") return {};
  const out = {};
  const logoRaw = user.companylogobase65 || user.logoBase65;
  out.logoUrl =
    typeof logoRaw === "string" && logoRaw
      ? String(logoRaw).startsWith("data:")
        ? String(logoRaw)
        : `data:image/png;base64,${logoRaw}`
      : resolveAgentCompanyLogoUrl(user);
  const agency = String(user.companyname || user.companyName || "").trim();
  const fn = String(user.firstname || user.firstName || "").trim();
  const ln = String(user.lastname || user.lastName || "").trim();
  const name = `${fn} ${ln}`.trim();
  out.agencyName = agency || null;
  out.agentName = name || null;
  out.phone = String(user.cell || user.phone || user.Phone || "").trim() || null;
  out.email = String(user.email || user.Email || "").trim() || null;
  out.whatsapp = String(user.whatsapp || user.WhatsApp || "").trim() || null;
  const hasAny = Object.values(out).some(Boolean);
  return hasAny ? out : {};
}

function toFiniteIls(v) {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * חבילה אחרי HR בלי דמי טיפול ובלי עמלת אשראי.
 * מחיר ההצעה ללקוח לא משתמש בזה לבד — הוא זהה ל«לתשלום» בפרופיל.
 */
export function resolveOrganizedTourQuoteBaselineIls({
  selectedResult,
  ourPrice,
  calculateLDSDealFareForRoomsComposition,
} = {}) {
  const fromCard = resolveOrganizedTourSearchCardPackageIls({
    root: selectedResult,
    raw: selectedResult,
    deal: selectedResult,
    calculateLDSDealFareForRoomsComposition,
  });
  if (fromCard != null) return fromCard;

  const otf =
    ourPrice?.ourTotalFare && typeof ourPrice.ourTotalFare === "object"
      ? ourPrice.ourTotalFare
      : selectedResult?.ourPrice?.ourTotalFare && typeof selectedResult.ourPrice.ourTotalFare === "object"
        ? selectedResult.ourPrice.ourTotalFare
        : null;
  const sanitized = sanitizeOrganizedTourPackageIls(otf, ourPrice);
  if (sanitized != null) return sanitized;

  return (
    toFiniteIls(otf?.priceWithCommission) ??
    toFiniteIls(ourPrice?.priceWithCommission) ??
    null
  );
}

/**
 * מחיר שיוצג ללקוח: אותו סכום כמו «לתשלום» בפרופיל
 * (מחיר חי אחרי HR + דמי טיפול וביטוח נסיעות פעם אחת), ואז התאמת סוכן.
 * עמלת אשראי לא נכללת. לא משתמשים במחיר העסקה המקורי כשהרכב החדרים כבר שינה אותו.
 */
export function computeOrganizedTourUiQuotePricing({
  selectedResult,
  ourPrice,
  orgTourQuoteOverride,
  calculateLDSDealFareForRoomsComposition,
  agentHrPercent,
}) {
  const displayedIls = resolveOrganizedTourProfileDisplayIls({
    ourPrice,
    selectedResult,
    calculateLDSDealFareForRoomsComposition,
    agentHrPercent,
  });
  let baselineCheckout = displayedIls;
  if (baselineCheckout == null) {
    const packageIls = resolveOrganizedTourQuoteBaselineIls({
      selectedResult,
      ourPrice,
      calculateLDSDealFareForRoomsComposition,
    });
    const handleFee = toFiniteIls(ourPrice?.handleFee) ?? 0;
    baselineCheckout = packageIls == null ? null : packageIls + handleFee;
  }
  return computeOrganizedTourAgentQuoteCommission({
    selectedResult,
    baselineCheckoutIls: baselineCheckout,
    override: orgTourQuoteOverride,
  });
}

/**
 * Snapshot מלא להצעה ציבורית / PDF — אותן שדות כמו במודאל «הצעת מחיר חכמה».
 *
 * @param {object} opts
 * @param {string} opts.termsSourceText
 */
export function buildOrganizedTourQuoteSnapshotPayload(opts) {
  const {
    selectedResult,
    ourPrice,
    orgTourQuoteOverride,
    itinerarySourceText,
    customerName,
    customerPhone,
    customerEmail,
    branding,
    dealId,
    roomsAndPaxInfo = null,
    termsSourceText = ORGANIZED_TOUR_QUOTE_TERMS_FALLBACK_HE,
    calculateLDSDealFareForRoomsComposition,
    agentHrPercent,
  } = opts;

  const quote = computeOrganizedTourUiQuotePricing({
    selectedResult,
    ourPrice,
    orgTourQuoteOverride,
    calculateLDSDealFareForRoomsComposition,
    agentHrPercent,
  });
  const cardVm = selectedResult ? mapLdsDealToCardVm(selectedResult) : null;
  const supplierMeta = {
    supplierName: selectedResult?.supplierDetails?.supplierName,
    supplierCode: selectedResult?.supplierDetails?.supplierCode,
    supplierId:
      selectedResult?.supplierDetails?.supplierId ?? selectedResult?.supplierId,
  };

  const payload = buildOrganizedTourSmartOfferPayload({
    selectedResult,
    cardVm,
    supplierMeta,
    itinerarySourceText: itinerarySourceText || "",
    termsSourceText,
    finalQuotePriceIls: quote.finalQuotePrice,
    customerName,
    customerPhone,
    customerEmail,
    branding,
    dealId: dealId || selectedResult?.dealId || selectedResult?.ldsDealId,
    additionalInfoSourceText: selectedResult?.clientRemarks ?? "",
    roomsAndPaxInfo,
  });

  return { payload, finalQuotePrice: quote.finalQuotePrice };
}

/**
 * שמירת snapshot בשרת (קישור ציבורי) — ללא חובה להציג את הטוקן.
 */
export async function postOrganizedTourQuoteSnapshotToServer(payloadBuilt, finalPriceIls) {
  const user = getUserInfo() || {};
  const sid = user.sessionID || user.sessionId;
  if (!sid) {
    throw new Error("no_session");
  }
  return createPublicOfferLinkRequest({
    sessionID: sid,
    agent_id: user.id ?? user.agentId ?? null,
    payload: payloadBuilt,
    final_price_ils: finalPriceIls,
    ttl_hours: 48,
  });
}

export { buildAgentBrandingFromUser };
